/**
 * Find Time walker — resolves a user's mode selection into a
 * concrete list of `YYYY-MM-DD` date keys.
 *
 * **Anchor is always today**, never the visible calendar week. That
 * way clicking "Find time this week" while navigated to some future
 * week still targets the user's actual current week; the container
 * then navigates the calendar to the first resolved date.
 *
 * Inputs:
 *   mode           — the Find Time option the user picked.
 *   nowMs          — current time (tests pass a frozen epoch).
 *   timezone       — drives day boundaries.
 *   availableDays  — optional. When provided, only dates in this set
 *                    are picked. When omitted, walker falls back to
 *                    "weekdays" (Mon–Fri) — the legacy behaviour for
 *                    callers that don't have prefetched availability
 *                    data yet.
 *
 * Output:
 *   `{ dateKeys, firstDateKey, noAvailability? }`
 *     - `dateKeys` are what `applyFindTime` dispatches `toggleDay(true)`
 *       on. `firstDateKey` drives the calendar's `gotoDate` when the
 *       selection spans a future week.
 *     - `noAvailability: true` is set ONLY when `availableDays` was
 *       provided AND the walker found nothing. Caller can show an
 *       "interviewers not available for this option" popover. When
 *       `availableDays` is omitted the flag is never set (legacy
 *       behaviour).
 *
 * ## Availability mode
 *
 * Earlier we deliberately skipped consulting availability — it had
 * been coupled to a single visible-week cache that fell apart on
 * navigation. With `useCommonAvailabilityRange` now prefetching
 * multiple weeks ahead, we can pass that data in and have the walker
 * pick only days the interviewers are actually free. PTO + custom
 * schedules + multi-interviewer overlap all fall out of one
 * membership check.
 */
import moment from 'moment-timezone';
import type { FindTimeValue } from './modes';
import { weekdaysTargetFor } from './modes';

// ─────────────────────────────────────────────────────────────────
// Tuning constants.
// ─────────────────────────────────────────────────────────────────

/**
 * Legacy fallback: `0 = Sun, 1 = Mon, … 6 = Sat`. A day is a
 * "weekday" iff its `day()` value is in this set. Mon–Fri.
 *
 * Used only when `availableDays` is not passed (callers without
 * prefetched availability data). Once every caller threads
 * availability through, this constant + the fallback branch can go.
 */
const WEEKDAY_DOW = new Set<number>([1, 2, 3, 4, 5]);

/**
 * Hard cap on how many days the walker will search forward when
 * `availableDays` is provided. Prevents an infinite loop if the
 * caller passes an empty set. ~12 weeks easily covers our 4-week
 * prefetch horizon.
 */
const MAX_LOOKAHEAD_DAYS = 84;

// ─────────────────────────────────────────────────────────────────
// Small helpers.
// ─────────────────────────────────────────────────────────────────

function dateKey(m: moment.Moment): string {
  return m.format('YYYY-MM-DD');
}

/**
 * "Should this day be picked?" — single source of truth.
 * Availability mode dominates when `availableDays` is provided;
 * otherwise we fall back to the legacy weekday gate.
 */
function isPickable(
  cursor: moment.Moment,
  availableDays?: ReadonlySet<string>
): boolean {
  if (availableDays) return availableDays.has(dateKey(cursor));
  return WEEKDAY_DOW.has(cursor.day());
}

// ─────────────────────────────────────────────────────────────────
// Public API.
// ─────────────────────────────────────────────────────────────────

export interface WalkArgs {
  mode: Exclude<FindTimeValue, null>;
  nowMs: number;
  timezone: string;
  /**
   * Days with at least one bookable slot, keyed `YYYY-MM-DD` in
   * `timezone`. When provided, the walker picks only these days
   * and signals `noAvailability` if nothing matched. Omit to fall
   * back to "weekdays only" — legacy behaviour kept for callers
   * that don't yet have prefetched availability data.
   */
  availableDays?: ReadonlySet<string>;
}

export interface WalkResult {
  /** Dates to auto-fill. */
  dateKeys: string[];
  /** Earliest date — drives calendar navigation. */
  firstDateKey: string | null;
  /**
   * `true` only when `availableDays` was provided AND zero days
   * were picked. Caller surfaces "interviewers not available for
   * this option" UI in this case. When `availableDays` is omitted
   * (legacy fallback) this is never set.
   */
  noAvailability?: boolean;
}

export function walkFindTime({
  mode,
  nowMs,
  timezone,
  availableDays,
}: WalkArgs): WalkResult {
  let result: WalkResult;
  if (mode === 'this_week') {
    result = walkThisWeek(nowMs, timezone, availableDays);
  } else {
    const weekdaysTarget = weekdaysTargetFor(mode);
    if (weekdaysTarget !== null) {
      result = walkNextWeekdays(nowMs, timezone, weekdaysTarget, availableDays);
    } else if (mode === 'next_week') {
      result = walkCalendarWeeks(nowMs, timezone, 1, availableDays);
    } else if (mode === 'next_2_weeks') {
      result = walkCalendarWeeks(nowMs, timezone, 2, availableDays);
    } else {
      result = { dateKeys: [], firstDateKey: null };
    }
  }
  // Empty-result + availability-aware → flag for the UI. Don't flag
  // in legacy fallback mode (Saturday-today case has always returned
  // empty there and we don't want to suddenly start nagging users).
  if (availableDays && result.dateKeys.length === 0) {
    return { ...result, noAvailability: true };
  }
  return result;
}

// ─────────────────────────────────────────────────────────────────
// Walker — "this week".
//
// Today (inclusive) through the last day of the current calendar
// week (Saturday in our Sun–Sat regime). Picks only days that
// pass `isPickable` — `availableDays` membership when provided,
// otherwise the weekday gate.
// ─────────────────────────────────────────────────────────────────

function walkThisWeek(
  nowMs: number,
  timezone: string,
  availableDays?: ReadonlySet<string>
): WalkResult {
  const today = moment.tz(nowMs, timezone).startOf('day');
  const weekEnd = today.clone().endOf('week'); // Saturday end
  const dateKeys: string[] = [];
  const cursor = today.clone();
  while (cursor.isSameOrBefore(weekEnd, 'day')) {
    if (isPickable(cursor, availableDays)) dateKeys.push(dateKey(cursor));
    cursor.add(1, 'day');
  }
  return { dateKeys, firstDateKey: dateKeys[0] ?? null };
}

// ─────────────────────────────────────────────────────────────────
// Walker — "next N weekdays".
//
// Tomorrow onwards, pick the next N pickable days. In availability
// mode, "pickable" means present in `availableDays`; the walker
// stops after `MAX_LOOKAHEAD_DAYS` if it can't find N matches so
// it never spins. In legacy mode, "pickable" is Mon–Fri.
// ─────────────────────────────────────────────────────────────────

function walkNextWeekdays(
  nowMs: number,
  timezone: string,
  target: number,
  availableDays?: ReadonlySet<string>
): WalkResult {
  const cursor = moment.tz(nowMs, timezone).add(1, 'day').startOf('day');
  const dateKeys: string[] = [];
  let stepped = 0;
  while (dateKeys.length < target && stepped < MAX_LOOKAHEAD_DAYS) {
    if (isPickable(cursor, availableDays)) dateKeys.push(dateKey(cursor));
    cursor.add(1, 'day');
    stepped += 1;
  }
  return { dateKeys, firstDateKey: dateKeys[0] ?? null };
}

// ─────────────────────────────────────────────────────────────────
// Walker — "next N calendar weeks".
//
// Sunday of next calendar week → weeks × 7 days. In availability
// mode, only days present in `availableDays` are kept. In legacy
// mode every day in the range is kept (the pipeline used to filter
// at render time).
// ─────────────────────────────────────────────────────────────────

function walkCalendarWeeks(
  nowMs: number,
  timezone: string,
  weeks: number,
  availableDays?: ReadonlySet<string>
): WalkResult {
  const firstWeekStart = moment
    .tz(nowMs, timezone)
    .add(1, 'week')
    .startOf('week');
  const dateKeys: string[] = [];
  for (let w = 0; w < weeks; w += 1) {
    const weekStart = firstWeekStart.clone().add(w, 'week');
    for (let d = 0; d < 7; d += 1) {
      const cursor = weekStart.clone().add(d, 'day');
      if (availableDays) {
        if (availableDays.has(dateKey(cursor))) dateKeys.push(dateKey(cursor));
      } else {
        // Legacy fallback: pick every day in the range (the rendering
        // pipeline filters out days with no availability there).
        dateKeys.push(dateKey(cursor));
      }
    }
  }
  return { dateKeys, firstDateKey: dateKeys[0] ?? null };
}
